import { generatePostBlockHTML } from '$lib/ai/post/utils/generatePostBlockHTML';
import type { MessageContent } from '@AlidaPackages/ailabs-client';
import type { PostChild } from '$lib/api/restApi';
import { parseAICopywriterResponse } from '$lib/types/AICopywriter/api';
import {
	isValidContextBlockType,
	type AICopywriterPost,
	type AICopywriterPostChild,
} from '$lib/types/AICopywriter/types';

const getPostChildren = (
	aiCopywriterPost: AICopywriterPost,
	editorPostChildren?: AICopywriterPostChild[]
) => {
	const blocks = aiCopywriterPost.blocks;
	const postChildren: PostChild[] = [];

	let hasErrors = false;

	blocks.forEach((block) => {
		// Replaces context blocks with editor post children if they match
		if (block.type === 'context') {
			if (!isValidContextBlockType(block.content) || !editorPostChildren) {
				hasErrors = true;
				return;
			}

			const matchingEditorChild = editorPostChildren.find((child) => child.id === block.id);
			if (!matchingEditorChild) {
				hasErrors = true;
				return;
			}

			// Remove the id from the editor child to match the PostChild type
			const { id: _, ...restChild } = matchingEditorChild;
			postChildren.push({
				...restChild,
				body: restChild.body || '', // Some PostChild types such as galleries do not have a body, so we ensure body is defined
				children: restChild.children || [], // Ensure children is defined as well
			} satisfies PostChild);
		} else {
			// Handles text blocks (titles, subtitles, paragraphs, ordered lists, unordered lists, blockquotes, code blocks)
			const postChildBody = generatePostBlockHTML(block);
			if (!postChildBody) {
				hasErrors = true;
				return;
			}

			postChildren.push({
				type: 'text',
				body: postChildBody,
				children: [],
			} satisfies PostChild);
		}
	});

	// If there are any null blocks return null to indicate an AI response error
	return hasErrors ? null : postChildren;
};

export const copywriterMessagesToPostChildren = (
	messages: MessageContent[],
	editorPostChildren?: AICopywriterPostChild[]
) => {
	const postMessage = messages.find((message) => message.type === 'json');

	// No json message found
	if (!postMessage) {
		return null;
	}

	const parsedPostMessage = parseAICopywriterResponse(postMessage);

	// Message is not a valid AICopywriterResponseMessage
	if (!parsedPostMessage) {
		return null;
	}

	return getPostChildren(parsedPostMessage.text.post, editorPostChildren);
};
